import React, { useState } from "react";
import Select from 'react-select';

function BodyWeightCal() {
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [gender, setGender] = useState({value: "male", label: "Male"});
  const [result, setResult] = useState("");
  const [errors, setErrors] = useState({});

  // Height and weight limits
  const HEIGHT_MIN = 60; // Changed from 20 to 60
  const HEIGHT_MAX = 110;
  const WEIGHT_MIN = 2;
  const WEIGHT_MAX = 700;

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" }
  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      border: '1px solid #ced4da',
      borderRadius: '0.25rem',
      minHeight: '38px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#80bdff',
      }
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#495057',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
  };

  const validateInputs = () => {
    const newErrors = {};
    
    // Height validation
    if (!height) {
      newErrors.height = "Height is required";
    } else if (isNaN(height) || parseFloat(height) <= 0) {
      newErrors.height = "Please enter a valid positive number for height";
    } else {
      const heightVal = parseFloat(height);
      if (heightVal < HEIGHT_MIN) {
        newErrors.height = "IBW formulas are not reliable for heights under 60 inches. Please use our BMI Calculator to assess your healthy weight range";
      } else if (heightVal > HEIGHT_MAX) {
        newErrors.height = `Height must be less than ${HEIGHT_MAX} inches`;
      }
    }
    
    // Weight validation
    if (!weight) {
      newErrors.weight = "Weight is required";
    } else if (isNaN(weight) || parseFloat(weight) <= 0) {
      newErrors.weight = "Please enter a valid positive number for weight";
    } else {
      const weightVal = parseFloat(weight);
      if (weightVal < WEIGHT_MIN) {
        newErrors.weight = `Weight must be at least ${WEIGHT_MIN} kg`;
      } else if (weightVal > WEIGHT_MAX) {
        newErrors.weight = `Weight must be less than ${WEIGHT_MAX} kg`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const IdealBodyWeight = () => {
    if (!validateInputs()) return;
    
    let IBM;
    const heightVal = parseFloat(height);

    if (gender.value === "male") {
      IBM = 50 + 2.3 * (heightVal - 60);
    } else if (gender.value === "female") {
      IBM = 45.5 + 2.3 * (heightVal - 60);
    }

    // Ensure IBM is not negative
    IBM = Math.max(IBM, 0);
    
    setResult(`Ideal Body Weight (IBW): ${IBM.toFixed(2)} kg`);
  };

  const resetForm = () => {
    setHeight("");
    setWeight("");
    setGender({value: "male", label: "Male"});
    setResult("");
    setErrors({});
  };

  // Handle input change with validation
  const handleInputChange = (e, setter, fieldName, min, max) => {
    const value = e.target.value;
    
    // Allow empty input for backspace/delete
    if (value === '') {
      setter(value);
      
      // Clear error for this field
      if (errors[fieldName]) {
        const newErrors = {...errors};
        delete newErrors[fieldName];
        setErrors(newErrors);
      }
      return;
    }
    
    // Check if it's a valid positive number
    if (!isNaN(value) && parseFloat(value) >= 0) {
      setter(value);
      
      // Validate against limits
      const numValue = parseFloat(value);
      if (numValue < min || numValue > max) {
        setErrors({
          ...errors,
          [fieldName]: `${fieldName === 'height' ? 'Height' : 'Weight'} must be between ${min} and ${max}`
        });
      } else {
        // Clear error for this field if value is valid
        if (errors[fieldName]) {
          const newErrors = {...errors};
          delete newErrors[fieldName];
          setErrors(newErrors);
        }
      }
    }
  };

  // Handle key press to prevent negative characters
  const handleKeyPress = (e) => {
    // Prevent minus sign, comma, and 'e' for exponent notation
    if (e.key === '-' || e.key === ',' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  return (
    <>
      <div id="body_weight_tab py-4">
        <div className="row mt-2">
          <div className="col-md-6">
            <h3 className="mb-3" style={{ color: "#333", fontSize: "1.75rem" }}>Ideal Body Weight</h3>
            <p style={{ fontSize: 16, textAlign: "justify" }}>
              Ideal Body Weight (IBW) is an estimate of how much you should weigh based on your height and gender. 
              It's commonly used in medical settings to determine medication dosages and nutritional needs.
            </p>
            <p style={{ fontSize: 16, textAlign: "justify" }}>
              The Devine formula is one of the most commonly used methods to calculate IBW:
            </p>
            <ul>
              <li className="list">Males: IBW = 50 kg + 2.3 kg for each inch over 5 feet</li>
              <li className="list">Females: IBW = 45.5 kg + 2.3 kg for each inch over 5 feet</li>
            </ul>
            <p className="text-muted" style={{ fontStyle: "italic" }}>
              Note: This formula is applicable for adults with a height of 5 feet (60 inches) or more.
            </p>
            <div className="clearfix">
              <br />
            </div>
          </div>
          <div className="col-md-6">
            <div style={{ width: "100%" }}>
              <div className="body-fat-ratio-heading">
                <h5>Ideal Body Weight Calculator</h5>
              </div>
              <div style={{ backgroundColor: "#F7F7F7" }}>
                <div className="clearfix">
                  <br />
                </div>
                <form
                  style={{
                    width: "100%",
                    padding: "0px 10px",
                    marginTop: "20px",
                  }}
                  id="IdealBodyWeightForm"
                >
                  <div className="row" style={{ borderBottom: "0px" }}>
                    <div className="col-lg-12 mb-3">
                      <label
                        className="control-label"
                        htmlFor="height"
                      >
                        <strong>Height In Inches*</strong>
                      </label>
                      <input
                        className={`form-control mb-0 ${errors.height ? 'is-invalid' : ''}`}
                        id="height"
                        name="height"
                        placeholder={`${HEIGHT_MIN}-${HEIGHT_MAX}`}
                        type="number"
                        min={HEIGHT_MIN}
                        max={HEIGHT_MAX}
                        step="0.1"
                        value={height}
                        onChange={(e) => handleInputChange(e, setHeight, 'height', HEIGHT_MIN, HEIGHT_MAX)}
                        onKeyPress={handleKeyPress}
                      />
                      {errors.height && <div className="invalid-feedback mt-0" style={{lineHeight:"1.3rem"}}>{errors.height}</div>}
                    </div>
                    <div className="col-lg-12 mb-3">
                      <label
                        className="control-label"
                        htmlFor="weight"
                      >
                        <strong>Weight In kg*</strong>
                      </label>
                      <input
                        className={`form-control mb-0 ${errors.weight ? 'is-invalid' : ''}`}
                        id="weight"
                        name="weight"
                        placeholder={`${WEIGHT_MIN}-${WEIGHT_MAX}`}
                        type="number"
                        min={WEIGHT_MIN}
                        max={WEIGHT_MAX}
                        step="0.1"
                        value={weight}
                        onChange={(e) => handleInputChange(e, setWeight, 'weight', WEIGHT_MIN, WEIGHT_MAX)}
                        onKeyPress={handleKeyPress}
                      />
                      {errors.weight && <div className="invalid-feedback mt-0">{errors.weight}</div>}
                    </div>
                    <div className="col-lg-12 mb-3">
                      <label
                        className="control-label mb-2"
                        htmlFor="gender"
                      >
                        <strong>Gender*</strong>
                      </label>
                      <Select
                        options={genderOptions}
                        value={gender}
                        onChange={setGender}
                        styles={customStyles}
                        isSearchable={false}
                        inputId="gender"
                      />
                    </div>
                    <div className="col-lg-12 col-lg-offset-3 d-flex justify-content-around mt-4">
                      <button
                        type="button"
                        className=" calculate "
                        onClick={IdealBodyWeight}
                      >
                        Calculate
                      </button>
                      <button
                        type="button"
                        className="value-clear-btn"
                        onClick={resetForm}
                      >
                        Reset
                      </button>
                    </div>
                    <div
                      id="IdealBodyWeightResult"
                      style={{ textAlign: "center", fontSize: "20px" }}
                      className="mt-3 p-2"
                    >
                      {result}
                    </div>
                    <div className="mt-3 p-3" >
                      <h6><strong>Formula:</strong></h6>
                      <p className="mb-1">For males: IBW = 50 kg + 2.3 kg × (height in inches - 60)</p>
                      <p className="mb-0">For females: IBW = 45.5 kg + 2.3 kg × (height in inches - 60)</p>
                    </div>
                    <div className="clearfix">
                      <br />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .calculate, .clear {
          min-width: 120px;
          padding: 8px 16px;
        }
        .invalid-feedback {
          display: block;
          color: #dc3545;
          font-size: 80%;
          margin-top: 0.25rem;
        }
        .is-invalid {
          border-color: #dc3545 !important;
        }
        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { 
          -webkit-appearance: none; 
          margin: 0; 
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
    </>
  );
}

export default BodyWeightCal;